import { LoadingOverlay } from "@mantine/core";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { queryClient } from "../../../App";
import { AuthContext } from "../../../providers/AuthProvider";
import Employer from "../../auth/services/model/Employer";
import useGetMetabaseUrlApiService from "../services/api/GetMetabaseUrlApiService";
import useGetMultiEmployerService from "../services/api/GetMultiEmployerService";

export interface EmployerReportContextType {
  employer: Employer | null;
  selectedEmployers: number[];
  setSelectedEmployers: (selectedEmployers: number[]) => void;
  metabaseDashboardUrl: string;
  employers: Employer[];
}
export const EmployerReportContext =
  React.createContext<EmployerReportContextType>({
    employer: null,
    employers: [],
    selectedEmployers: [],
    setSelectedEmployers: (): void | null => null,
    metabaseDashboardUrl: "",
  });

export const useEmployerReport = (): EmployerReportContextType =>
  useContext(EmployerReportContext);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const EmployerReportProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const auth = useContext(AuthContext);
  const [employer] = useState<Employer | null>(auth?.employer || null);
  const [selectedEmployers, setSelectedEmployers] = useState<number[]>([]);
  const [metabaseDashboardUrl, setMetabaseDashboardUrl] = useState<string>("");

  const branchesService = useGetMultiEmployerService();
  const metabaseService = useGetMetabaseUrlApiService(
    Array.from(new Set(selectedEmployers.concat(employer?.id ?? []))).join(",")
  );

  useEffect(() => {
    queryClient.invalidateQueries([
      "GetMetabaseUrlApiService",
      selectedEmployers.join(","),
    ]);
  }, [selectedEmployers]);

  useEffect(() => {
    setSelectedEmployers(
      ((branchesService.data?.employers?.length ?? 0) > 0
        ? branchesService.data?.employers?.map((branch: Employer) => branch.id)
        : []) as number[]
    );
  }, [branchesService.data]);

  useEffect(() => {
    setMetabaseDashboardUrl(metabaseService?.data?.url ?? "");
  }, [metabaseService.data]);

  const data = useMemo(
    () => ({
      employer,
      selectedEmployers,
      setSelectedEmployers,
      metabaseDashboardUrl,
      employers:
        branchesService?.data?.employers ||
        (auth?.employer ? [auth.employer] : []),
    }),
    [employer, branchesService, auth?.employer]
  );
  if (!auth) {
    return <Navigate replace to="/login" />;
  }
  return (
    <EmployerReportContext.Provider value={data}>
      <>
        <LoadingOverlay
          visible={branchesService.isLoading || metabaseService.isLoading}
        />
        {children}
      </>
    </EmployerReportContext.Provider>
  );
};

export default EmployerReportProvider;
